import pluralize from 'pluralize';
import { ICollectionDescription } from '../types';

/**
 * Retrieves the collection description based on the given text.
 * @param text - The input text.
 * @returns The collection description object.
 */
export function getCollectionDescription(text: string): ICollectionDescription {
  text = toValidCollectionName(text);
  const displayName = toDisplayName(text);
  const pluralName = toPluralName(text);
  const pluralDisplayName = toDisplayName(pluralName);
  const singularName = text;
  const title = toTitleCase(text);
  return {
    displayName,
    pluralName,
    pluralDisplayName,
    singularName,
    title,
  };
}

/**
 * Converts a camel case string to snake case.
 * @param text - The input text.
 * @returns The converted snake case string.
 */
export function camelToSnakeCase(text: string) {
  return text.replace(/[A-Z]/g, (letter: string, index: number) => {
    return index > 0 ? `_${letter.toLowerCase()}` : letter.toLowerCase();
  });
}

/**
 * Checks if the given collection description is valid.
 * @param description - The collection description object.
 * @returns True if the collection description is valid, false otherwise.
 */
export function isValidCollectionDescription(description: ICollectionDescription): boolean {
  return description.singularName !== description.pluralName;
}

/**
 * Checks if the given collection description is reserved.
 * @param description - The collection description object.
 * @returns True if the collection description is reserved, false otherwise.
 */
export function isReservedCollectionDescription(description: ICollectionDescription): boolean {
  return [
    'category',
    'label',
    'locale',
    'market',
    'menu',
    'redirect',
    'route',
    'template',
  ].includes(description.singularName);
}

/**
 * Converts a string to a display name by removing spaces.
 * @param text - The input text.
 * @returns The converted display name.
 */
export function toDisplayName(text: string) {
  return toTitleCase(text).replace(/\s/g, '');
}

/**
 * Converts a string to its plural form.
 * @param text - The input text.
 * @returns The plural form of the input text.
 */
export function toPluralName(text: string) {
  // pluralize.addPluralRule(/News$/i, 'Newses');
  // pluralize.addSingularRule(/Newses$/i, 'News');
  const plural = pluralize.plural(text);
  return plural === text ? `${plural}es` : plural;
}

/**
 * Converts a string to title case.
 * @param text - The input text.
 * @returns The converted title case string.
 */
export function toTitleCase(text: string) {
  return text
    .replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()) // Initial char (after -/_)
    .replace(/[-_]+(.)/g, (_, c) => ' ' + c.toUpperCase()); // First char after each -/_
}

/**
 * Converts a string to a valid collection name by removing special characters and converting to snake case.
 * @param text - The input text.
 * @returns The converted valid collection name.
 */
export function toValidCollectionName(text: string): string {
  text = text
    .trim()
    .replace(/\W/g, '_')
    .replace(/^[_]*(.)/, (_, c) => c);
  text = camelToSnakeCase(text);
  text = text.replace(/__/g, '_');
  return text.toLowerCase();
}
